/* eslint-disable no-param-reassign */
/* Reatribuir propriedade de parâmetro é o idioma do `traverse` da three e
 * dos acumuladores de cor: é assim que se escreve em uma malha ou em um
 * vetor de saída já alocado. */
/*
 * Modelo 3D do transformador construído EM CÓDIGO, peça por peça, com as cotas
 * portadas do protótipo `feature/visualizador-transformador-weg.html`.
 *
 * O par deste módulo é transformadorCad.js, que carrega a malha real do CAD de
 * arquivo. Os dois devolvem o MESMO formato de modelo (ver contrato.js), e é
 * isso que permite ao viewer tratá-los sem saber de onde vieram.
 *
 * Saiu de CmaAsset3dView.vue sem mudança de comportamento: os builders eram
 * métodos do componente e liam `this.materials`; agora recebem os materiais por
 * parâmetro (`m`), que é a única dependência que tinham do componente.
 */
import * as THREE from 'three';
import {
  aplicarUvDeMundo, clonarTextura, remapearRugosidade,
  aplicarMapaDeCor, aplicarIntemperiePorAltura,
} from '@/domain/components/cma/cmaAssetTextures';
import {
  box, cyl, cylAxis, ring, roundedRect, pipeBetween, pipeRun,
  annulus, cone, insulator, finBank, srgbColor, TAU,
} from './geometriaHelpers';
import { HIGHLIGHT_TARGET } from './contrato';

const TANK_W = 8.6;
const TANK_D = 3.6;
const TANK_H = 4.4;
const TANK_Y0 = 0.55;
const LID_Y = TANK_Y0 + TANK_H;
const RAD_X = TANK_W / 2;
const HV_X = [-2.7, 0, 2.7];

const SILICA_BANDAS = 7;
const SILICA_SATURADA = 0.34;
const SILICA_SECA = 0x2f6fd0;
const SILICA_UMIDA = 0xd4547e;

const TEX_PINTURA = {
  mapas: 'chapa',
  uv: 0.60,
  relevo: 0.35,
  ao: [2.40, 0.28],
  variacao: 0.10,
};
const TEX_PINTURA_CLARA = {
  mapas: 'chapa',
  uv: 0.28,
  relevo: 0.28,
  ao: [1.10, 0.16],
  variacao: 0.08,
};
const TEX_METAL = {
  mapas: 'metal',
  uv: 0.16,
  relevo: 0.85,
  ao: [0.55, 0.18],
  variacao: 0.15,
};
const TEX_TUBO = {
  mapas: 'metal',
  uv: 0.16,
  relevo: 0.40,
  variacao: 0.08,
};
const TEX_PINTURA_TOPO = {
  mapas: 'chapa',
  uv: 1.05,
  relevo: 0.30,
  ao: [2.40, 0.34],
  variacao: 0.14,
};
const TEX_LATAO = {
  mapas: 'metal',
  uv: 0.09,
  relevo: 0.55,
  variacao: 0.12,
};
const TEX_CABO = {
  mapas: 'chapa',
  uv: 0.05,
  relevo: 0.30,
  variacao: 0.06,
};
const TEX_PORCELANA = { mapas: 'porcelana', uv: 0.40, variacao: 0.05 };


const TEXTURE_MAP = {
  tanque: TEX_PINTURA,
  chapa: TEX_PINTURA,
  radiador: TEX_PINTURA,
  escuro: TEX_PINTURA,
  chapaClara: TEX_PINTURA,
  painelEscuro: TEX_PINTURA,
  tampa: TEX_PINTURA_TOPO,
  painelRad: TEX_PINTURA_CLARA,
  flange: TEX_METAL,
  terminal: TEX_METAL,
  placa: TEX_METAL,
  latao: TEX_LATAO,
  tubo: TEX_TUBO,
  preto: TEX_CABO,
  borracha: TEX_CABO,
  porcelana: TEX_PORCELANA,
};

const INTEMPERIE_RESPINGO = { ate: 1.15, fator: [0.87, 0.845, 0.80] };
const INTEMPERIE_BEIRAL = { centro: LID_Y - 0.30, meiaLargura: 0.55, fator: [0.91, 0.925, 0.92] };
const INTEMPERIE_SOL = { de: 3.90, ate: 7.20, fator: [1.075, 1.055, 1.015] };

/*
 * Exportada para o carregador do CAD (transformadorCad.js) poder aplicar a
 * MESMA regra de intemperismo por altura do modelo procedural (item 1 da
 * spec cma-modelo-3d-cad-texturas-explosao.md) — as faixas (respingo, beiral,
 * sol) foram calibradas para a altura do procedural (~8 m); no CAD (9,5 m)
 * ficam levemente deslocadas proporcionalmente, efeito aceito como
 * aproximação (mesmo padrão de "calibrar em tela depois" usado em toda a
 * spec para vetores de explosão e âncoras).
 */
export function intemperieNaAltura(y, saida) {
  const suave = (t) => {
    const c = Math.min(1, Math.max(0, t));
    return c * c * (3 - 2 * c);
  };

  const respingo = 1 - suave(y / INTEMPERIE_RESPINGO.ate);
  const beiral = 1 - suave(
    Math.abs(y - INTEMPERIE_BEIRAL.centro) / INTEMPERIE_BEIRAL.meiaLargura,
  );
  const sol = suave(
    (y - INTEMPERIE_SOL.de) / (INTEMPERIE_SOL.ate - INTEMPERIE_SOL.de),
  );

  for (let c = 0; c < 3; c += 1) {
    saida[c] = (1 + (INTEMPERIE_RESPINGO.fator[c] - 1) * respingo)
      * (1 + (INTEMPERIE_BEIRAL.fator[c] - 1) * beiral)
      * (1 + (INTEMPERIE_SOL.fator[c] - 1) * sol);
  }
}

const PANEL_ENV_MAP_INTENSITY = 0.80;

export const GAUGE_MIN_ANGLE = 0;
export const GAUGE_MAX_ANGLE = Math.PI / 2;
export const GAUGE_SWEEP_PERIOD = 7.5;
export const GAUGE_TREMOR_AMPLITUDE = 0.030;
export const GAUGE_TREMOR_PERIOD = 0.150;

export const BOLT_TRAVEL = 950;
export const BOLT_INTERVAL = [2500, 7000];
export const BOLT_FROM_Y = 0.66;
export const BOLT_TO_Y = 2.31;
const BOLT_OFFSET = 0.34;
const BOLT_TURNS = 2;
export const BOLT_PITCH = (BOLT_TO_Y - BOLT_FROM_Y) / BOLT_TURNS;
const BOLT_RISE = 0.24;
const BOLT_STEPS = 10;
const BOLT_JAG = 0.030;
const BOLT_JAG_ANGLE = 0.085;
const BOLT_SEED = 20260905;
const BOLT_TAIL = 0.34;
const BOLT_BRANCH_AT = 0.55;
const BOLT_BRANCH_STEPS = 2;
const BOLT_BRANCH_SPREAD = 0.070;
const BOLT_BRANCH_RISE = 0.055;
export const BOLT_FLICKER_STEPS = 22;
export const BOLT_EMISSIVE = 3.0;
const BOLT_RADIUS = 0.013;

const boltPrng = (semente) => {
  let estado = semente % 2147483647;
  if (estado <= 0) estado += 2147483646;
  return () => {
    estado = (estado * 16807) % 2147483647;
    return estado / 2147483647;
  };
};

export const boltEase = t => t * (0.4 + 0.6 * t);

/*
 * Ventiladores do banco de refrigeração (ver buildPanelFans): três, pendurados
 * num berço sob os painéis do radiadorB e soprando PARA CIMA, através deles.
 *
 * O corpo sai de buildFanUnit, montado em raio 1 — prato de montagem, virola,
 * grade de arames, seis pás e nariz cônico, copiados de uma foto de
 * referência. `PLATE_RADIUS` é o raio do prato nessa escala unitária, e é ele
 * que decide o tamanho final, porque é a parte mais larga do conjunto.
 *
 * `HOUSING_RADIUS` é a cota herdada da carcaça que existia aqui antes: 0,46 m
 * de raio, a 0,95 m uma da outra. É ela que prende a escala — com 2 × 0,46
 * contra os 0,95 do espaçamento sobram 3 cm entre vizinhos, e um raio maior
 * faria os pratos se atravessarem.
 */
const FAN_PLATE_RADIUS = 1.12;
const FAN_HOUSING_RADIUS = 0.46;
/*
 * Exportada para o builder de ventiladores do Transformador 1 procedural
 * (transformadorProceduralCad.js) reaproveitar a mesma proporção entre a
 * unidade em raio 1 (buildFanUnit) e o raio final do ventilador na cena,
 * sem duplicar a conta.
 */
export const FAN_SCALE = FAN_HOUSING_RADIUS / FAN_PLATE_RADIUS;
const FAN_X = [0, 1, 2].map(i => RAD_X + 1.05 + (i * 0.95));
const FAN_Z = 1.05;
/*
 * Base do ventilador na altura em que ficava o FUNDO da carcaça antiga (ela
 * era centrada em TANK_Y0 - 0,08 e tinha 0,26 de altura). O plano de admissão
 * continua onde estava e o corpo cresce para cima, na direção em que sopra.
 */
const FAN_BASE_Y = (TANK_Y0 - 0.08) - 0.13;
const FAN_BLADE_PITCH = 0.42;
const FAN_SHROUD = 0.68;
const FAN_GUARD_CROWN = 0.14;
const FAN_GUARD_RINGS = [0.20, 0.36, 0.52, 0.68, 0.84];
const FAN_RIBS = 8;
const FAN_BOLTS = 12;
const FAN_BLADES = 6;
const FAN_SPEEDS = [5.15, 4.85, 5.42];

const boltPath = () => {
  const sorteio = boltPrng(BOLT_SEED);

  const naHelice = (y, raio, desvio) => {
    const angulo = (TAU * y) / BOLT_PITCH + desvio;
    return [Math.cos(angulo) * raio, y, Math.sin(angulo) * raio];
  };

  const passos = [];
  let total = 0;
  for (let i = 0; i < BOLT_STEPS; i += 1) {
    const passo = 0.45 + sorteio();
    passos.push(passo);
    total += passo;
  }

  const trilha = [];
  let percorrido = 0;
  let lado = 1;
  for (let i = 0; i <= BOLT_STEPS; i += 1) {
    const u = percorrido / total;
    const y = u * BOLT_RISE;
    const barriga = Math.sin(Math.PI * u) ** 0.55;
    const raio = BOLT_OFFSET + lado * BOLT_JAG * (0.30 + sorteio() * 0.70) * barriga;
    const desvio = lado * BOLT_JAG_ANGLE * (0.25 + sorteio() * 0.75) * barriga;
    trilha.push(naHelice(y, raio, desvio));
    if (sorteio() > 0.17) lado = -lado;
    percorrido += passos[i] || 0;
  }

  const raiz = trilha[Math.round(BOLT_STEPS * BOLT_BRANCH_AT)];
  const raioDaRaiz = Math.hypot(raiz[0], raiz[2]);
  const ramo = [raiz];
  for (let i = 1; i <= BOLT_BRANCH_STEPS; i += 1) {
    const passo = i / BOLT_BRANCH_STEPS;
    const y = raiz[1] + BOLT_BRANCH_RISE * passo;
    const torto = (i % 2 === 0 ? 1 : -1) * BOLT_JAG * 0.7 * (1 - passo);
    ramo.push(naHelice(y, raioDaRaiz + BOLT_BRANCH_SPREAD * passo + torto, 0));
  }

  return { trilha, ramo };
};

const PART_EXPLODE = {
  base: [0, -2.0, 0],
  tanque: [0, 0, 0],
  tampa: [0, 1.9, 0],
  buchasAT: [0, 2.4, 2.4],
  buchasBT: [0, 2.6, 0],
  pararaios: [0, 1.7, -0.7],
  conservador: [-2.4, 2.8, 0],
  radiadorA: [3.45, 0.45, 0],
  radiadorB: [4.5, 0, 0.6],
  ventiladores: [5.55, -0.68, 0.75],
  comutador: [-3.8, 0, 0],
  escada: [-3.0, 0, 2.0],
  painel: [0, 0, -2.8],
  termometro: [0, 0, -2.8],
  rele: [-2.4, 2.8, 0],
};

const ANCHOR_DEFS = [
  { id: 'buchas', part: 'buchasAT', point: [0, 8.5, 5.6] },
  { id: 'comutador', part: 'comutador', point: [-5.0, 2.5, -0.5] },
  { id: 'gas', part: 'rele', point: [-2.0, 5.6, 0.1] },
  { id: 'tensaoCarga', part: 'termometro', point: [1.05, 3.45, -2] },
  { id: 'bolsa', part: 'conservador', point: [-3.3, 7.1, -0.2] },
  { id: 'tensao', part: 'buchasBT', point: [1.9, 7.4, -0.4] },
  { id: 'eletricos', part: 'painel', point: [-2.5, 2.3, -2.15] },
  { id: 'resfriamento', part: 'radiadorB', point: [5.9, 2.2, 1.0] },
];

function aplicarTexturas(materials, textures) {
    const normalDe = { chapa: textures.chapaNormal, metal: textures.metalNormal };
  const roughnessDe = {
    chapa: textures.chapaRoughness,
    metal: textures.metalRoughness,
    porcelana: textures.porcelanaRoughness,
  };
  const albedoDe = {
    chapa: textures.chapaAlbedo,
    metal: textures.metalAlbedo,
    porcelana: textures.porcelanaAlbedo,
  };
  const metalnessDe = { metal: textures.metalMetalness };

  Object.keys(TEXTURE_MAP).forEach((nome) => {
    const material = materials[nome];
    if (!material) return;
    const config = TEXTURE_MAP[nome];

    aplicarMapaDeCor(
      THREE, material, albedoDe[config.mapas],
      textures.mediasAlbedo[config.mapas], config.uv,
    );

    const rugosidade = remapearRugosidade(
      THREE, roughnessDe[config.mapas], material.roughness, config.variacao, config.uv,
    );
    if (rugosidade) {
      material.roughnessMap = rugosidade;
      material.roughness = 1;
    }

    const normal = clonarTextura(THREE, normalDe[config.mapas], config.uv);
    if (normal && config.relevo !== undefined) {
      material.normalMap = normal;
      material.normalScale = new THREE.Vector2(config.relevo, config.relevo);
    }

    const metalness = clonarTextura(THREE, metalnessDe[config.mapas], config.uv);
    if (metalness) material.metalnessMap = metalness;

    if (config.ao) {
      const [metrosAo, intensidadeAo] = config.ao;
      material.aoMap = clonarTextura(THREE, textures.ruidoAO, metrosAo);
      material.aoMapIntensity = intensidadeAo;
    }

    material.needsUpdate = true;
  });
}

export function criarMateriais(textures) {
  const steel = (color, roughness, metalness) => new THREE.MeshStandardMaterial({
    color: srgbColor(color), roughness, metalness,
  });
  const painted = (color, roughness) => steel(color, roughness, 0);
  const materials = {
    tanque: painted(0x6a6f74, 0.62),
    tampa: painted(0x6d7176, 0.74),
    chapa: painted(0x62676c, 0.64),
    radiador: painted(0x676c70, 0.70),
    escuro: painted(0x2a2e31, 0.68),
    painelRad: painted(0x9a9ba0, 0.55),
    flange: steel(0x9b9b9b, 0.62, 1),
    preto: steel(0x232323, 0.62, 0),
    chapaClara: painted(0x73787d, 0.58),
    painelEscuro: painted(0x121516, 0.55),
    latao: steel(0xb08d57, 0.34, 1),
    raio: new THREE.MeshStandardMaterial({
      color: srgbColor(0x000000),
      emissive: srgbColor(0xffd23a),
      emissiveIntensity: BOLT_EMISSIVE,
      roughness: 1,
    }),
    tela: new THREE.MeshStandardMaterial({
      color: srgbColor(0x0b3f3a),
      emissive: srgbColor(0x00a96b),
      emissiveIntensity: 0.55,
      roughness: 0.16,
      metalness: 0.12,
    }),
    ledVerde: new THREE.MeshStandardMaterial({
      color: srgbColor(0x31d478),
      emissive: srgbColor(0x20b866),
      emissiveIntensity: 1.4,
      roughness: 0.20,
    }),
    ledAmbar: new THREE.MeshStandardMaterial({
      color: srgbColor(0xffaa22),
      emissive: srgbColor(0xd56c00),
      emissiveIntensity: 1.25,
      roughness: 0.20,
    }),
    prateado: steel(0xd6d6d6, 0.16, 1),
    metalPolido: steel(0xc4c8cb, 0.10, 1),
    agulha: steel(0x1c1f21, 0.35, 0),
    placa: steel(0xc4c4c4, 0.30, 1),
    terminal: steel(0xb6b6b6, 0.42, 1),
    borracha: steel(0x232323, 0.72, 0),
    tubo: steel(0x76797d, 0.55, 1),
    porcelana: new THREE.MeshPhysicalMaterial({
      color: srgbColor(0x3d2621),
      roughness: 0.12,
      metalness: 0,
      clearcoat: 0.92,
      clearcoatRoughness: 0.045,
    }),
    porcelanaBucha: new THREE.MeshPhysicalMaterial({
      color: srgbColor(0xcc1a1a),
      roughness: 0.12,
      metalness: 0,
      clearcoat: 0.92,
      clearcoatRoughness: 0.045,
    }),
    visor: new THREE.MeshPhysicalMaterial({
      color: srgbColor(0x9fd3d6),
      roughness: 0.06,
      metalness: 0,
      transparent: true,
      opacity: 0.62,
    }),
  };

  const frente = SILICA_SATURADA * SILICA_BANDAS;
  materials.silica = [];
  for (let i = 0; i < SILICA_BANDAS; i += 1) {
    const saturacao = Math.max(0, Math.min(1, frente - i));
    const cor = new THREE.Color(srgbColor(SILICA_SECA))
      .lerp(new THREE.Color(srgbColor(SILICA_UMIDA)), saturacao);
    materials.silica.push(new THREE.MeshStandardMaterial({
      color: cor,
      roughness: 0.78,
      metalness: 0,
    }));
  }

  Object.keys(TEXTURE_MAP).forEach((nome) => {
    if (materials[nome]) materials[nome].vertexColors = true;
  });

  materials.prateado.userData.envMap = PANEL_ENV_MAP_INTENSITY;

  if (textures) aplicarTexturas(materials, textures);
  return materials;
}

function buildBase(m) {
  const g = new THREE.Group();
    [-1.45, 1.45].forEach((z) => {
    g.add(box(TANK_W + 0.5, 0.38, 0.55, m.escuro, 0, 0.19, z));
    g.add(box(TANK_W + 0.7, 0.12, 0.30, m.chapa, 0, 0.40, z));
  });
  [-3.4, 0, 3.4].forEach(x => g.add(box(0.45, 0.32, 3.2, m.escuro, x, 0.16, 0)));
  [-3.3, 3.3].forEach((x) => {
    [-1.45, 1.45].forEach((z) => {
      g.add(box(0.9, 0.22, 0.8, m.chapa, x, 0.12, z));
      [-0.28, 0.28].forEach(dz => g.add(cylAxis(0.2, 0.14, m.escuro, x, 0.1, z + dz, 'x', 16)));
    });
  });
  [-3.95, 3.95].forEach((x) => {
    [-1.7, 1.7].forEach(z => g.add(box(0.18, 0.5, 0.5, m.chapa, x, 0.5, z)));
  });
  return g;
}

function buildTank(m) {
  const g = new THREE.Group();
    const tank = box(TANK_W, TANK_H, TANK_D, m.tanque, 0, TANK_Y0 + TANK_H / 2, 0);
  tank.name = HIGHLIGHT_TARGET;
  g.add(tank);

  for (let i = -4; i <= 4; i += 1) {
    [-TANK_D / 2 - 0.09, TANK_D / 2 + 0.09].forEach((z) => {
      g.add(box(0.20, TANK_H - 0.35, 0.18, m.chapa, i * 0.95, TANK_Y0 + TANK_H / 2 - 0.1, z));
    });
  }
  [-1.15, 0, 1.15].forEach((z) => {
    [-TANK_W / 2 - 0.09, TANK_W / 2 + 0.09].forEach((x) => {
      g.add(box(0.18, TANK_H - 0.35, 0.20, m.chapa, x, TANK_Y0 + TANK_H / 2 - 0.1, z));
    });
  });
  [TANK_Y0 + 1.15, TANK_Y0 + 3.15].forEach((y) => {
    g.add(box(TANK_W + 0.30, 0.14, TANK_D + 0.30, m.chapa, 0, y, 0));
  });
  g.add(box(TANK_W + 0.12, 0.18, TANK_D + 0.12, m.borracha, 0, TANK_Y0 + 0.1, 0));

  g.add(cylAxis(0.13, 0.5, m.tubo, -3.6, 1.1, TANK_D / 2 + 0.30, 'z', 14));
  g.add(cylAxis(0.22, 0.16, m.flange, -3.6, 1.1, TANK_D / 2 + 0.52, 'z', 16));
  g.add(box(0.5, 0.8, 0.3, m.chapa, 3.2, 1.3, TANK_D / 2 + 0.22));
  return g;
}

function buildLid(m) {
  const g = new THREE.Group();
    g.add(box(TANK_W + 0.18, 0.22, TANK_D + 0.18, m.tampa, 0, LID_Y + 0.11, 0));
  g.add(box(TANK_W + 0.34, 0.10, TANK_D + 0.34, m.flange, 0, LID_Y + 0.02, 0));
  [-2.7, 0, 2.7].forEach((x) => {
    g.add(box(0.22, 0.26, TANK_D - 0.1, m.chapa, x, LID_Y + 0.33, 0));
  });
  [-1.1, 1.1].forEach(z => g.add(box(TANK_W - 0.6, 0.22, 0.24, m.chapa, 0, LID_Y + 0.33, z)));
  [-1.4, 1.4].forEach((x) => {
    g.add(cyl(0.42, 0.14, m.flange, x, LID_Y + 0.28, -1.3, 24));
    g.add(cyl(0.34, 0.08, m.chapa, x, LID_Y + 0.36, -1.3, 24));
  });
  return g;
}

/*
 * Exportada para o carregador do CAD reaproveitar a animação de eletricidade
 * (item 10 da spec) nas buchas de baixa tensão do CAD (Bushing_S1/S2/S3) sem
 * duplicar a matemática do raio — é 100% sintética (não usa nada do modelo
 * procedural em si) e já recebe os materiais por parâmetro.
 */
export function buildCurrentBolt(m) {
  const pivo = new THREE.Group();
  const percurso = new THREE.Group();
  const { trilha, ramo } = boltPath();
  for (let i = 0; i < trilha.length - 1; i += 1) {
    const cabeca = (i + 1) / (trilha.length - 1);
    const espessura = BOLT_RADIUS * (BOLT_TAIL + (1 - BOLT_TAIL) * cabeca);
    percurso.add(pipeBetween(trilha[i], trilha[i + 1], espessura, m.raio, 5));
  }
  for (let i = 0; i < ramo.length - 1; i += 1) {
    percurso.add(pipeBetween(ramo[i], ramo[i + 1], BOLT_RADIUS * BOLT_TAIL, m.raio, 4));
  }
  pivo.add(percurso);
  pivo.visible = false;
  pivo.userData.percurso = percurso;
  return pivo;
}

function buildHvBushings(m) {
  const g = new THREE.Group();
    HV_X.forEach((x) => {
    const b = new THREE.Group();
    b.position.set(x, TANK_Y0 + 2.35, TANK_D / 2 - 0.15);
    b.rotation.x = 0.62;
    b.add(cyl(0.56, 2.5, m.tanque, 0, 1.25, 0, 28));
    b.add(cyl(0.66, 0.12, m.flange, 0, 0.12, 0, 28));
    b.add(cyl(0.66, 0.14, m.flange, 0, 2.48, 0, 28));
    b.add(cyl(0.60, 0.10, m.flange, 0, 1.30, 0, 28));
    b.add(cylAxis(0.30, 0.10, m.chapa, 0, 1.30, 0.58, 'z', 20));
    b.add(cyl(0.46, 0.26, m.terminal, 0, 2.68, 0, 24));
    const ins = insulator(3.3, 0.18, 0.32, 22, m.porcelana, 24);
    ins.position.y = 2.80;
    b.add(ins);
    b.add(cyl(0.24, 0.40, m.terminal, 0, 6.32, 0, 20));
    b.add(cyl(0.13, 0.34, m.terminal, 0, 6.68, 0, 16));
    b.add(cyl(0.055, 0.55, m.flange, 0, 7.10, 0, 12));
    g.add(b);
  });
  return g;
}

function buildLvBushings(m) {
  const g = new THREE.Group();
    [0.7, 1.9, 3.1].forEach((x, i) => {
    const b = new THREE.Group();
    b.position.set(x, LID_Y + 0.22, -0.55 + i * 0.10);
    b.add(cyl(0.44, 0.55, m.tanque, 0, 0.28, 0, 24));
    b.add(cyl(0.54, 0.12, m.flange, 0, 0.56, 0, 24));
    const ins = insulator(1.35, 0.15, 0.27, 11, m.porcelana, 20);
    ins.position.y = 0.64;
    b.add(ins);
    b.add(cyl(0.18, 0.28, m.terminal, 0, 2.12, 0, 18));
    b.add(cyl(0.05, 0.30, m.flange, 0, 2.40, 0, 10));
    b.add(buildCurrentBolt(m));
    g.add(b);
  });
  return g;
}

function buildSurgeArresters(m) {
  const g = new THREE.Group();
    [0.4, 1.9].forEach((x) => {
    const p = new THREE.Group();
    p.position.set(x, LID_Y + 0.22, 0.78);
    p.add(cyl(0.24, 0.18, m.flange, 0, 0.09, 0, 18));
    const ins = insulator(0.75, 0.11, 0.21, 7, m.porcelana, 18);
    ins.position.y = 0.18;
    p.add(ins);
    p.add(cyl(0.10, 0.16, m.flange, 0, 1.02, 0, 14));
    g.add(p);
  });
  return g;
}

function buildConservator(m) {
  const g = new THREE.Group();

  const body = cylAxis(0.85, 3.6, m.tanque, -3.3, 7.05, 0.1, 'z', 32);
  body.name = HIGHLIGHT_TARGET;
  g.add(body);
  [-1.1, 1.1].forEach(dz => g.add(cylAxis(0.88, 0.10, m.flange, -3.3, 7.05, 0.1 + dz, 'z', 30)));

  const CONSERVATOR_BOLT_COUNT = 16;
  [-1.8, 1.8].forEach((dz) => {
    for (let i = 0; i < CONSERVATOR_BOLT_COUNT; i += 1) {
      const angle = (i / CONSERVATOR_BOLT_COUNT) * Math.PI * 2;
      const bx = -3.3 + Math.cos(angle) * 0.78;
      const by = 7.05 + Math.sin(angle) * 0.78;
      const bz = 0.1 + dz + (dz > 0 ? 0.04 : -0.04);
      g.add(cylAxis(0.045, 0.08, m.flange, bx, by, bz, 'z', 8));
    }
  });

  const gaugeZ = 0.1 - 1.8;
  g.add(cylAxis(0.27, 0.19, m.prateado, -3.3, 7.05, gaugeZ - 0.11, 'z', 24));
  g.add(cylAxis(0.22, 0.05, m.prateado, -3.3, 7.05, gaugeZ - 0.20, 'z', 24));
  const needlePivot = new THREE.Group();
  needlePivot.position.set(-3.3, 7.05, gaugeZ - 0.24);
  needlePivot.rotation.z = GAUGE_MIN_ANGLE;
  needlePivot.userData.repouso = GAUGE_MIN_ANGLE;
  needlePivot.add(box(0.20, 0.03, 0.02, m.agulha, 0.10, 0, 0));
  needlePivot.add(cylAxis(0.035, 0.03, m.prateado, 0, 0, 0, 'z', 12));
  g.add(needlePivot);

  g.add(box(1.5, 2.0, 0.22, m.chapa, -3.3, 5.95, 0.1));
  g.add(box(0.22, 1.9, 1.9, m.chapa, -3.3, 5.95, 0.1));
  [-1, 1].forEach((s) => {
    const strut = box(0.16, 2.6, 0.16, m.chapa, -3.3 + s * 0.95, 5.85, 0.1 + s * 0.55);
    strut.rotation.z = s * 0.30;
    g.add(strut);
  });

  const pipe = cyl(0.17, 2.0, m.tubo, -2.45, 6.15, 0.1, 16);
  pipe.rotation.z = -0.55;
  g.add(pipe);
  const lowerPipe = cyl(0.17, 0.9, m.tubo, -1.72, 5.22, 0.1, 16);
  lowerPipe.rotation.z = -0.95;
  g.add(lowerPipe);

  const secadorX = -4.40;
  const secadorZ = 1.25;
  const colunaBase = 5.05;
  const colunaAltura = 1.1;
  const banda = colunaAltura / SILICA_BANDAS;
  for (let i = 0; i < SILICA_BANDAS; i += 1) {
    g.add(cyl(
      0.125, banda, m.silica[i],
      secadorX, colunaBase + banda * (i + 0.5), secadorZ, 14,
    ));
  }
  const meio = colunaBase + colunaAltura / 2;
  g.add(cyl(0.16, colunaAltura, m.visor, secadorX, meio, secadorZ, 14));
  g.add(cyl(0.22, 0.14, m.flange, secadorX, 6.22, secadorZ, 14));
  g.add(cyl(0.19, 0.26, m.visor, secadorX, 4.92, secadorZ, 14));
  g.add(cyl(0.155, 0.12, m.preto, secadorX, 4.86, secadorZ, 14));
  return g;
}

function buildBuchholzRelay(m) {
  const g = new THREE.Group();
    g.add(box(0.46, 0.42, 0.42, m.flange, -1.98, 5.58, 0.1));
  g.add(cylAxis(0.13, 0.10, m.visor, -1.98, 5.58, 0.34, 'z', 14));
  return g;
}

function buildRadiatorBank(m) {
  const g = new THREE.Group();
    const finGeometry = new THREE.BoxGeometry(0.055, 2.5, 1.9);
  g.add(finBank(26, 0.086, finGeometry, m.radiador, RAD_X + 0.60, TANK_Y0 + 3.00, -0.75));
  [TANK_Y0 + 4.30, TANK_Y0 + 1.72].forEach((y) => {
    g.add(cylAxis(0.16, 2.5, m.tubo, RAD_X + 1.70, y, -0.75, 'x', 14));
    g.add(cylAxis(0.15, 1.2, m.tubo, RAD_X + 0.30, y, -0.75, 'x', 14));
    g.add(cylAxis(0.24, 0.12, m.flange, RAD_X + 0.05, y, -0.75, 'x', 16));
  });
  g.add(box(0.12, 2.7, 2.1, m.chapa, RAD_X + 0.48, TANK_Y0 + 3.00, -0.75));
  return g;
}

function buildCoolingPanels(m) {
  const g = new THREE.Group();
    for (let i = 0; i < 5; i += 1) {
    const x = RAD_X + 0.78 + i * 0.46;
    g.add(box(0.30, 2.3, 1.5, m.painelRad, x, TANK_Y0 + 1.85, 1.05));
    g.add(box(0.34, 0.10, 1.6, m.chapa, x, TANK_Y0 + 3.05, 1.05));
    g.add(box(0.34, 0.10, 1.6, m.chapa, x, TANK_Y0 + 0.68, 1.05));
  }
  [TANK_Y0 + 3.15, TANK_Y0 + 0.60].forEach((y) => {
    g.add(cylAxis(0.15, 2.6, m.tubo, RAD_X + 1.45, y, 1.05, 'x', 14));
    g.add(cylAxis(0.14, 1.0, m.tubo, RAD_X + 0.22, y, 1.05, 'x', 14));
  });
  g.add(box(0.12, 2.6, 1.7, m.chapa, RAD_X + 0.48, TANK_Y0 + 1.85, 1.05));
  return g;
}

/**
 * Um ventilador, em RAIO 1 e com o eixo em +Y — a unidade que
 * buildPanelFans instancia três vezes.
 *
 * Raio 1, e não o raio final, porque quem instancia escala o grupo
 * inteiro: as cotas daqui são FRAÇÕES do raio, e os encaixes que elas
 * guardam — pá dentro do aro, nariz dentro da grade — continuam valendo em
 * qualquer porte. A escala é uniforme, o que importa: não uniforme
 * inverteria normais e distorceria os arames da grade.
 *
 * Eixo em +Y é o que deixa updateFans girar a hélice em `rotation.y`, e
 * também é o sentido em que ela sopra.
 *
 * Exportada para o builder de ventiladores do Transformador 1 procedural
 * (transformadorProceduralCad.js) reaproveitar a mesma unidade sem duplicar
 * a geometria — só troca a disposição das cópias (2, uma de cada lado do
 * tanque, em vez de 3 em fileira), ver buildCoolingFansCad.
 */
export function buildFanUnit(m, giro, fase) {
  const g = new THREE.Group();

  // prato de montagem e os parafusos em volta: a peça mais larga do
  // conjunto, e a que encosta no berço
  g.add(annulus(0.98, FAN_PLATE_RADIUS, 0.06, m.chapaClara, 0, 0.03, 0));
  for (let p = 0; p < FAN_BOLTS; p += 1) {
    const angulo = (p * TAU) / FAN_BOLTS;
    g.add(cyl(
      0.035, 0.09, m.flange,
      Math.cos(angulo) * 1.05, 0.055, Math.sin(angulo) * 1.05, 8,
    ));
  }

  // virola: do prato até a grade, e é dentro dela que a hélice gira
  g.add(annulus(0.97, 1.00, FAN_SHROUD, m.chapaClara, 0, FAN_SHROUD / 2, 0));

  /*
   * Motor no meio da virola, com três braços que o seguram nela.
   *
   * Fica ATRÁS da hélice, do lado da admissão: é o que deixa o giro à
   * vista de quem olha do lado em que o ar sai.
   */
  g.add(cyl(0.30, 0.28, m.chapaClara, 0, 0.20, 0, 24));
  g.add(cyl(0.34, 0.05, m.flange, 0, 0.365, 0, 24));
  for (let i = 0; i < 3; i += 1) {
    const braco = new THREE.Group();
    braco.rotation.y = (i * TAU) / 3;
    braco.add(box(0.67, 0.06, 0.10, m.chapaClara, 0.635, 0.18, 0));
    g.add(braco);
  }

  // caixa de ligação e o rabicho, sem passar do raio do prato: é ele que
  // define a caixa envolvente e o espaçamento entre vizinhos
  g.add(box(0.24, 0.22, 0.16, m.chapaClara, 0, 0.20, -1.03));
  g.add(cylAxis(0.05, 0.18, m.borracha, 0, 0.20, -1.03, 'z', 12));

  /*
   * A hélice, e só ela, gira: `userData.giro` é o que buildModel acha no
   * traverse e o que updateFans avança por tempo decorrido.
   *
   * A `fase` é fixa por índice, e não sorteada: o modelo é reconstruído a
   * cada montagem do dashboard, e um sorteio faria as variantes do
   * carrossel nascerem em ângulos diferentes a cada visita — o giro não é
   * aleatório, é só dessincronizado.
   */
  const helice = new THREE.Group();
  helice.userData.giro = giro;
  helice.rotation.y = fase;
  helice.position.y = 0.48;
  g.add(helice);
  helice.add(cyl(0.22, 0.16, m.flange, 0, 0, 0, 20));
  helice.add(cone(0.22, 0.20, m.chapaClara, 0, 0.18, 0));

  for (let b = 0; b < FAN_BLADES; b += 1) {
    const braco = new THREE.Group();
    braco.rotation.y = -(b * TAU) / FAN_BLADES;
    // pá larga, como as seis da referência: a corda passa da metade do
    // próprio comprimento
    const pa = box(0.60, 0.04, 0.46, m.chapaClara, 0.60, 0, 0);
    pa.rotation.x = FAN_BLADE_PITCH;
    braco.add(pa);
    helice.add(braco);
  }

  /*
   * Grade: aro de borda, costelas radiais e aros concêntricos.
   *
   * Cada costela é meia volta de arame (`arc` = π) no plano XY, que nasce
   * com as duas pontas na borda e a barriga em +Y. Uma geometria só serve
   * as oito: cada uma a reaproveita com `scale.y`, que achata o
   * abaulamento, e `rotation.y`, que as espalha em leque. As duas comutam
   * — a rotação é em torno do próprio Y que a escala mexe —, então nenhuma
   * depende da ordem em que o three compõe a matriz.
   */
  const costelaGeometry = new THREE.TorusGeometry(1, 0.018, 6, 40, Math.PI);
  const grade = new THREE.Group();
  grade.position.y = FAN_SHROUD;
  g.add(grade);

  grade.add(ring(1, 0.030, m.flange, 0, 0, 0, 40));
  for (let i = 0; i < FAN_RIBS; i += 1) {
    const costela = new THREE.Mesh(costelaGeometry, m.flange);
    costela.scale.y = FAN_GUARD_CROWN;
    costela.rotation.y = (i * Math.PI) / FAN_RIBS;
    grade.add(costela);
  }
  // cada aro pousa na altura em que o arco das costelas passa pelo raio
  // dele, para a grade ler como uma peça só
  FAN_GUARD_RINGS.forEach((r) => {
    grade.add(ring(r, 0.015, m.flange, 0, FAN_GUARD_CROWN * Math.sqrt(1 - (r * r)), 0, 28));
  });

  return g;
}

/**
 * Os três ventiladores do banco de refrigeração, no berço sob os painéis
 * do radiadorB (ver as constantes FAN_*).
 *
 * Sem rotação nenhuma: a unidade já nasce com o eixo em +Y, então ela
 * sopra de baixo para cima, através dos painéis — que é o que um
 * ventilador de radiador faz, e o que o berço acima deles pressupõe.
 */
function buildPanelFans(m) {
  const g = new THREE.Group();

  FAN_X.forEach((x, i) => {
    const ventilador = buildFanUnit(m, FAN_SPEEDS[i], i * 0.41);
    ventilador.scale.setScalar(FAN_SCALE);
    ventilador.position.set(x, FAN_BASE_Y, FAN_Z);
    g.add(ventilador);
  });

  /*
   * Berço, herdado do banco antigo junto com as cotas: os trilhos em
   * z = 0,55 e 1,55 passam rentes por FORA dos aros (raio 0,46 em torno de
   * z = 1,05, ou seja de 0,59 a 1,51), então seguram a borda sem
   * atravessar hélice nenhuma.
   */
  const topo = TANK_Y0 + 0.60;
  const meio = (FAN_BASE_Y + topo) / 2;
  [0.55, 1.55].forEach((z) => {
    g.add(cylAxis(0.05, 3.1, m.chapa, RAD_X + 1.95, FAN_BASE_Y + 0.31, z, 'x', 8));
  });
  [RAD_X + 0.55, RAD_X + 1.60, RAD_X + 2.65].forEach((x) => {
    [0.55, 1.55].forEach((z) => {
      g.add(box(0.07, topo - FAN_BASE_Y, 0.07, m.chapa, x, meio, z));
    });
  });
  return g;
}

function buildTapChanger(m) {
  const g = new THREE.Group();
    const cx = -TANK_W / 2 - 0.75;

  const body = cyl(0.80, 2.9, m.tanque, cx, TANK_Y0 + 1.9, -0.6, 26);
  body.name = HIGHLIGHT_TARGET;
  g.add(body);

  g.add(cyl(0.90, 0.14, m.flange, cx, TANK_Y0 + 3.32, -0.6, 26));
  g.add(cyl(0.86, 0.12, m.flange, cx, TANK_Y0 + 0.55, -0.6, 26));
  g.add(box(0.75, 1.15, 0.65, m.chapa, cx - 0.10, TANK_Y0 + 0.85, 1.10));
  g.add(box(0.06, 0.95, 0.50, m.flange, cx - 0.49, TANK_Y0 + 0.85, 1.10));
  const shaft = cyl(0.07, 1.7, m.tubo, cx - 0.05, TANK_Y0 + 1.60, 0.30, 10);
  shaft.rotation.x = 0.6;
  g.add(shaft);
  const pipe = cyl(0.12, 3.4, m.tubo, -3.95, TANK_Y0 + 4.40, -0.60, 12);
  pipe.rotation.z = 0.26;
  g.add(pipe);
  return g;
}

function buildLadder(m) {
  const g = new THREE.Group();
    const bx = -TANK_W / 2 - 0.30;
  [1.05, 1.55].forEach((z) => {
    const rail = cyl(0.06, 5.2, m.chapa, bx, TANK_Y0 + 2.3, z, 10);
    rail.rotation.z = 0.16;
    g.add(rail);
  });
  for (let i = 0; i < 11; i += 1) {
    const y = TANK_Y0 + 0.15 + i * 0.44;
    g.add(cylAxis(0.035, 0.5, m.chapa, bx - (y - TANK_Y0 - 2.3) * 0.16, y, 1.3, 'z', 8));
  }
  g.add(box(1.5, 0.07, 1.0, m.chapa, bx - 0.25, LID_Y + 0.55, 1.3));
  [-0.65, 0.65].forEach((dx) => {
    g.add(cyl(0.045, 1.0, m.chapa, bx - 0.25 + dx, LID_Y + 1.05, 1.75, 8));
  });
  g.add(cylAxis(0.045, 1.5, m.chapa, bx - 0.25, LID_Y + 1.55, 1.75, 'x', 8));
  return g;
}

function addMonitorTm1Face(m, g, x, y, front) {
    const face = front - 0.038;

  [0.072, -0.008].forEach((dy) => {
    g.add(box(0.34, 0.065, 0.014, m.preto, x, y + dy, front - 0.028));
    g.add(box(0.30, 0.045, 0.010, m.tela, x, y + dy, face));
  });

  [-0.09, -0.03, 0.03, 0.09].forEach((dx) => {
    g.add(cylAxis(0.020, 0.014, m.painelRad, x + dx, y - 0.088, face, 'z', 16));
  });
}

function buildControlPanel(m) {
  const g = new THREE.Group();
    const zf = -TANK_D / 2;
  const front = zf - 0.50;
  const conduitZ = front + 0.12;

  const CABINET_X = -2.6;
  const CABINET_Y = TANK_Y0 + 1.5;

  const cabinet = box(1.0, 1.35, 0.38, m.prateado, CABINET_X, CABINET_Y, zf - 0.30);
  cabinet.name = HIGHLIGHT_TARGET;
  g.add(cabinet);

  g.add(box(0.86, 1.15, 0.06, m.prateado, CABINET_X, CABINET_Y, front));
  g.add(box(0.68, 0.44, 0.03, m.painelEscuro, CABINET_X, CABINET_Y + 0.255, front - 0.05));
  g.add(box(0.62, 0.38, 0.02, m.visor, CABINET_X, CABINET_Y + 0.255, front - 0.075));

  [[-0.163, 0.347], [0.163, 0.347], [-0.163, 0.163], [0.163, 0.163]]
    .forEach(([dx, dy], index) => {
      const x = CABINET_X + dx;
      const y = CABINET_Y + dy;
      g.add(box(0.25, 0.13, 0.02, m.painelEscuro, x, y, front - 0.095));
      const screen = index === 3 ? m.ledAmbar : m.tela;
      g.add(box(0.16, 0.04, 0.015, screen, x, y + 0.013, front - 0.112));
    });

  [[-0.214, m.ledVerde], [-0.087, m.ledVerde], [0.041, m.ledVerde], [0.168, m.ledAmbar]]
    .forEach(([dx, material]) => {
      const x = CABINET_X + dx;
      g.add(cylAxis(0.024, 0.018, material, x, CABINET_Y - 0.128, front - 0.04, 'z', 16));
    });

  [-0.204, 0, 0.204].forEach((dx) => {
    const x = CABINET_X + dx;
    g.add(cylAxis(0.043, 0.023, m.painelEscuro, x, CABINET_Y - 0.316, front - 0.04, 'z', 18));
    g.add(box(0.023, 0.10, 0.018, m.prateado, x, CABINET_Y - 0.316, front - 0.062));
  });

  [-0.408, 0.408].forEach((dy) => {
    g.add(cyl(0.028, 0.143, m.prateado, CABINET_X + 0.479, CABINET_Y + dy, front - 0.01, 12));
  });
  g.add(box(
    0.041, 0.194, 0.051, m.painelEscuro,
    CABINET_X - 0.444, CABINET_Y - 0.051, front - 0.04,
  ));

  g.add(cylAxis(0.20, 0.14, m.prateado, 2.0, TANK_Y0 + 2.9, zf - 0.16, 'z', 18));
  g.add(roundedRect(0.29, 0.13, 0.035, 0.04, m.preto, 2.0, TANK_Y0 + 2.9, zf - 0.24));
  g.add(box(0.55, 0.70, 0.28, m.prateado, 0.2, TANK_Y0 + 1.2, zf - 0.26));
  g.add(box(0.44, 0.57, 0.04, m.prateado, 0.2, TANK_Y0 + 1.2, zf - 0.42));

  g.add(box(0.53, 0.35, 0.025, m.prateado, -0.75, 1.25, zf - 0.05));
  [1.12, 1.21, 1.30, 1.39].forEach((y) => {
    g.add(box(0.38, 0.011, 0.010, m.painelEscuro, -0.75, y, zf - 0.07));
  });

  /* Os dois monitores pendurados na frente do tanque. A caixa e o frontal
   * escuro são iguais nos dois; só o de cima leva a frente do TM1, com dois
   * displays e as teclas (ver addMonitorTm1Face). O de baixo segue como
   * monitor genérico de display único. */
  [3.15, 2.25].forEach((y, index) => {
    g.add(box(0.69, 0.55, 0.32, m.prateado, -1.45, y, zf - 0.30));
    g.add(box(0.50, 0.32, 0.045, m.painelEscuro, -1.45, y, front));
    if (index === 0) {
      addMonitorTm1Face(m, g, -1.45, y, front);
      return;
    }
    g.add(box(0.27, 0.07, 0.02, m.tela, -1.45, y + 0.04, front - 0.035));
    g.add(cylAxis(0.03, 0.025, m.ledVerde, -1.63, y - 0.11, front - 0.04, 'z', 12));
  });

  g.add(cyl(0.15, 0.55, m.prateado, -3.85, 1.35, zf - 0.26, 18));
  g.add(cyl(0.18, 0.10, m.latao, -3.85, 1.66, zf - 0.26, 18));
  g.add(cylAxis(0.042, 0.32, m.latao, -3.85, 1.74, zf - 0.26, 'x', 12));

  [-0.316, -0.153, 0.010, 0.173, 0.337].forEach((dx) => {
    const x = CABINET_X + dx;
    g.add(cyl(0.038, 0.092, m.borracha, x, 1.344, zf - 0.30, 12));
    g.add(cyl(0.023, 0.173, m.prateado, x, 1.232, zf - 0.30, 10));
  });

  pipeRun(g, [
    [-1.45, 1.97, conduitZ], [-1.45, 1.78, conduitZ], [-0.10, 1.78, conduitZ],
  ], 0.032, m.prateado);
  pipeRun(g, [
    [-1.45, 3.43, conduitZ], [-1.45, 3.62, conduitZ], [-2.15, 3.62, conduitZ],
  ], 0.032, m.prateado);
  pipeRun(g, [
    [0.20, 1.38, conduitZ], [0.20, 1.16, conduitZ], [-2.26, 1.16, zf - 0.30],
  ], 0.032, m.prateado);

  return g;
}

/**
 * Frente do TM1: dois displays empilhados e a fileira de quatro teclas de
 * navegação na base, nada mais — o que o equipamento mostra de longe.
 *
 * Sem textura, como o resto do modelo: nesta escala o frontal tem 0,50 ×
 * 0,32, então dígitos e rótulos não caberiam — cada elemento é a peça de
 * geometria que o representa, e a leitura vem do arranjo, não do desenho.
 *
 * As cotas são todas relativas ao centro do frontal (`x`, `y`) e o conjunto
 * fica centrado nele: sobram ~0,055 de margem em cima e embaixo. `front` é
 * o Z do frontal escuro, e `face` o plano dos elementos, um pouco à frente
 * da placa (Z mais negativo é para fora do tanque).
 */
function buildOilThermometer(m) {
  const g = new THREE.Group();
    const zf = -TANK_D / 2;
  g.add(cylAxis(0.20, 0.14, m.prateado, 1.2, TANK_Y0 + 2.9, zf - 0.16, 'z', 18));
  g.add(roundedRect(0.29, 0.12, 0.035, 0.04, m.preto, 1.2, TANK_Y0 + 2.9, zf - 0.24));
  return g;
}

/**
 * Quantos radianos girar de `from` para `to` andando no sentido `direction`
 * (+1 avança na ordem dos slots, -1 recua). Sem sentido declarado, cai no
 * arco curto — que é indefinido quando os slots ficam a meia volta, e por
 * isso o `direction` existe.
 *
 * `carouselAngleFor` é negativo em `index`, então AVANÇAR é girar para o
 * ângulo negativo: o sinal do delta é o oposto do sinal do sentido.
 */

export function criarModeloProcedural(spec, contexto) {
  /* Os materiais vêm prontos do viewer (ver criarMateriais): são um só jogo
   * para todas as variantes do carrossel, e é o que os builders recebem como
   * `m`. `deslocamentoX` e `envMapIntensity` são cotas da CENA, não do modelo,
   * então entram por aqui em vez de virarem constante deste módulo. */
  const { materials: m, deslocamentoX, envMapIntensity } = contexto;
  const group = new THREE.Group();
  const parts = {};

  const builders = {
    base: buildBase,
    tanque: buildTank,
    tampa: buildLid,
    buchasAT: buildHvBushings,
    buchasBT: buildLvBushings,
    pararaios: buildSurgeArresters,
    conservador: buildConservator,
    radiadorA: buildRadiatorBank,
    radiadorB: buildCoolingPanels,
    ventiladores: buildPanelFans,
    comutador: buildTapChanger,
    escada: buildLadder,
    painel: buildControlPanel,
    termometro: buildOilThermometer,
    rele: buildBuchholzRelay,
  };

  Object.keys(builders).forEach((id) => {
    const partGroup = builders[id](m);
    partGroup.name = id;
    partGroup.userData.home = new THREE.Vector3();
    partGroup.userData.explode = new THREE.Vector3().fromArray(PART_EXPLODE[id]);
    group.add(partGroup);
    parts[id] = partGroup;
  });

  group.position.x = deslocamentoX;

  group.updateMatrixWorld(true);

  group.traverse((object) => {
    if (object.isMesh) {
      object.castShadow = true;
      object.receiveShadow = true;
      if (object.geometry && !object.geometry.userData.uvDeMundo) {
        aplicarUvDeMundo(object.geometry);
        object.geometry.userData.uvDeMundo = true;
      }
      if (object.geometry) {
        const e = object.matrixWorld.elements;
        const cota = `${e[1]}|${e[5]}|${e[9]}|${e[13]}`;
        if (object.geometry.userData.intemperie !== cota) {
          if (object.geometry.userData.intemperie) {
            object.geometry = object.geometry.clone();
          }
          aplicarIntemperiePorAltura(object.geometry, e, intemperieNaAltura);
          object.geometry.userData.intemperie = cota;
        }
      }
    }
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    materials.forEach((material) => {
      if (!material) return;
      material.envMapIntensity = material.userData.envMap === undefined
        ? envMapIntensity
        : material.userData.envMap;
    });
  });

  const anchors = ANCHOR_DEFS.map(def => ({
    id: def.id,
    part: parts[def.part],
    point: new THREE.Vector3().fromArray(def.point),
  }));

  const helices = [];
  const ponteiros = [];
  const raios = [];
  group.traverse((object) => {
    if (object.userData.giro) helices.push(object);
    if (object.userData.repouso !== undefined) ponteiros.push(object);
    if (object.userData.percurso) raios.push(object);
  });

  return {
    id: spec.id, group, parts, anchors, helices, ponteiros, raios,
  };
}
